import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FlaskConical, Eye, EyeOff, ArrowRight, Sparkles, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    referral_code: ''
  });
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const { login, register } = useAuth();

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
      setIsLogin(false);
      toast.info('Referral code applied! Sign up to get 3 bonus papers.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.email, formData.password, formData.name, formData.referral_code);
        toast.success('Account created successfully!');
        if (formData.referral_code) {
          toast.success('🎉 You got 3 bonus paper analyses!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      setForgotSent(true);
    } catch {
      toast.error('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col lg:flex-row">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-teal-900/40" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 rounded-2xl bg-teal-400/20 border border-teal-400/30">
                <FlaskConical className="w-8 h-8 text-teal-400" />
              </div>
              <span className="text-3xl font-heading font-bold text-white">Simplify</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-6">
              Decode Research.<br />
              <span className="text-teal-400">Accelerate Discovery.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-md">
              Transform complex research papers into clear, actionable insights.
              Powered by AI, designed for researchers across all fields.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-8 min-h-screen lg:min-h-0">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <div className="p-2 rounded-xl bg-teal-400/20 border border-teal-400/30">
              <FlaskConical className="w-6 h-6 text-teal-400" />
            </div>
            <span className="text-2xl font-heading font-bold text-white">Simplify</span>
          </div>

          <div className="glass rounded-2xl lg:rounded-3xl p-6 lg:p-8 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                {isForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-slate-400">
                {isForgotPassword ? 'Enter your email to get a reset link' : isLogin ? 'Sign in to continue your research' : 'Start your research journey'}
              </p>
            </div>

            {isForgotPassword ? (
              <div>
                {forgotSent ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-teal-500/20 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-teal-400" />
                    </div>
                    <p className="text-white font-medium mb-2">Check your email</p>
                    <p className="text-slate-400 text-sm">If that email exists, we've sent a reset link.</p>
                    <button
                      onClick={() => { setIsForgotPassword(false); setForgotSent(false); }}
                      className="mt-4 text-teal-400 text-sm hover:text-teal-300 transition-colors"
                    >
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <p className="text-slate-400 text-sm text-center mb-2">
                      Enter your email and we'll send you a reset link.
                    </p>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Email</Label>
                      <Input
                        type="email"
                        placeholder="you@research.edu"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-400 focus:ring-teal-400/20 h-12 rounded-xl"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-xl"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                      ) : 'Send Reset Link'}
                    </Button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setIsForgotPassword(false)}
                        className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                      >
                        Back to <span className="text-teal-400 font-medium">sign in</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {!isLogin && (
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Dr. Jane Smith"
                        value={formData.name}
                        onChange={handleInputChange}
                        required={!isLogin}
                        data-testid="register-name-input"
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-400 focus:ring-teal-400/20 h-12 rounded-xl"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@research.edu"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      data-testid="auth-email-input"
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-400 focus:ring-teal-400/20 h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        data-testid="auth-password-input"
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-400 focus:ring-teal-400/20 h-12 rounded-xl pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {isLogin && (
                    <div className="text-right -mt-2">
                      <button
                        type="button"
                        onClick={() => setIsForgotPassword(true)}
                        className="text-sm text-slate-400 hover:text-teal-400 transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  {!isLogin && formData.referral_code && (
                    <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center gap-3">
                      <Gift className="w-5 h-5 text-teal-400" />
                      <div>
                        <p className="text-sm font-medium text-teal-400">Referral Applied!</p>
                        <p className="text-xs text-slate-400">You'll get 3 bonus paper analyses</p>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="auth-submit-button"
                    className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-teal-500/25"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                    ) : (
                      <>
                        {isLogin ? 'Sign In' : 'Create Account'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                    data-testid="toggle-auth-mode"
                  >
                    {isLogin ? "Don't have an account? " : 'Already have an account? '}
                    <span className="text-teal-400 font-medium">{isLogin ? 'Sign up' : 'Sign in'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;